<script setup lang="ts">
import {trackUser} from './utils/productAnalytics';
import {apiFetch} from './composables/api/apiFetch';
import {useIntegrationConnectionCache} from './composables/useIntegrationConnectionCache';
import {usePlan} from './composables/organization/usePlan';
import {useActiveOrg} from './composables/organization/useActiveOrg';
import {useIcpDefinitionState} from './composables/useIcpDefinitionState';
import type {PlanObject} from './composables/organization/usePlan';
import type {Segment} from '~/types/segment';
import type {Organization} from '~/types/api';
import type {IcpDefinitionResponse} from '~/types/icp';

// Type definitions
interface PropelAuthUser {
  userId: string;
  email: string;
  firstName: string;
  lastName: string;
  createdAt: string;
  loginMethod: string;
  loginCount?: number;
  lastLoginAt?: string;
}

interface PropelAuthClient {
  getAuthenticationInfoOrNull: () => Promise<{user: PropelAuthUser} | null>;
}

interface ChameleonCachedData {
  segments: Segment[];
  isSlackConnected: boolean;
  isHubspotConnected: boolean;
  isSalesforceConnected: boolean;
  isLinkedinConnected: boolean;
  icpDefinition: IcpDefinitionResponse | null;
  timestamp: number;
}

interface ChameleonSDK {
  identify: (_userId: string, _traits: Record<string, unknown>) => void;
}

interface PylonEmailHashResponse {
  emailHash: string;
}

interface PylonChatSettings {
  app_id: string;
  email: string;
  name: string;
  email_hash: string;
}

interface WindowWithIntegrations extends globalThis.Window {
  chmln?: ChameleonSDK;
  pylon?: {
    chat_settings: PylonChatSettings;
  };
}

const {$propelAuth} = useNuxtApp();
const {ensureConnectionStatuses, isConnected: isIntegrationConnected} =
  useIntegrationConnectionCache();
const colorMode = useColorMode();
if (colorMode?.preference) {
  colorMode.preference = 'light'; // Despite these docs (https://ui.nuxt.com/getting-started/theming#dark-mode), some customers were still getting dark mode if system was set to it
}

// Check if we're in development based on URL
const isDev =
  typeof window !== 'undefined' &&
  (window.location.hostname === 'localhost' ||
    window.location.hostname === '127.0.0.1' ||
    window.location.hostname.includes('.local'));

// Log only on client side
if (process.client) {
  console.log('[Vector] Environment check:', {
    hostname: window.location.hostname,
    isDev,
    pylonEnabled: !isDev,
  });
}

const headScripts = [{src: 'https://js.stripe.com/v3'}];

useHead({
  meta: [{charset: 'utf-8'}, {name: 'viewport', content: 'width=device-width, initial-scale=1'}],
  link: [{rel: 'icon', href: '/img/favicon.png'}],
  htmlAttrs: {
    lang: 'en',
  },
  script: headScripts,
});

const title = 'Vector';
const description = 'Person-level site identity';

useSeoMeta({
  title,
  description,
  ogTitle: title,
  ogDescription: description,
});

const auth = await ($propelAuth as PropelAuthClient)?.getAuthenticationInfoOrNull();
const plan: PlanObject = await usePlan();
const org = useState<Organization>('org');
/** Shared with layouts (e.g. LinkedIn migration banner); null until resolved for signed-in users */
const linkedinIntegrationConnected = useState<boolean | null>(
  'linkedinIntegrationConnected',
  () => null
);

// Debug: Chameleon auth/data availability (helps diagnose prod identify issues)
if (process.client) {
  console.log('[Chameleon] Auth state:', {
    hasAuth: !!auth,
    hasUser: !!auth?.user,
    userId: auth?.user?.userId ?? null,
    email: auth?.user?.email ?? null,
  });
}

if (auth?.user) {
  trackUser(auth.user.userId, {
    email: auth.user.email,
    name: `${auth.user.firstName} ${auth.user.lastName}`,
    first_name: auth.user.firstName,
    last_name: auth.user.lastName,
    plan: plan.planName,
    is_annual_plan: plan.isAnnualPlan,
    has_paid_plan: plan.hasPaidPlan,
    user_created_at: auth.user.createdAt,
    login_method: auth.user.loginMethod,
  });

  // Chameleon user identification
  try {
    if (process.client) {
      console.log('[Chameleon] Identify flow starting for user:', auth.user.userId);
    }

    const {getActiveOrg} = await useActiveOrg();
    const activeOrgId = getActiveOrg();

    // Get user hash from backend for security (Chameleon requirement)
    const hashResult = (await apiFetch(`/users/${auth.user.userId}/hash`).catch(err => {
      if (process.client) {
        console.warn('[Chameleon] User hash API failed:', err);
      }
      return null;
    })) as {data?: string} | null;
    const userHash = hashResult?.data ?? null;

    if (process.client) {
      console.log('[Chameleon] User hash from API:', {
        received: !!userHash,
        hashLength: userHash?.length ?? 0,
      });
    }

    // Cache for Chameleon data to avoid refetching on every navigation
    const CACHE_DURATION_MS = 5 * 60 * 1000; // 5 minutes
    const chameleonCache = useState<ChameleonCachedData | null>('chameleonCache', () => null);

    // Check if cache is still valid (reject legacy entries missing recent payload fields)
    const isCacheValid =
      chameleonCache.value &&
      Date.now() - chameleonCache.value.timestamp < CACHE_DURATION_MS &&
      typeof chameleonCache.value.isLinkedinConnected === 'boolean' &&
      'icpDefinition' in chameleonCache.value;

    let segments: Segment[];
    let isSlackConnected: boolean;
    let isHubspotConnected: boolean;
    let isSalesforceConnected: boolean;
    let isLinkedinConnected: boolean;
    let icpDefinition: IcpDefinitionResponse | null;

    if (isCacheValid && chameleonCache.value) {
      // Use cached data
      segments = chameleonCache.value.segments;
      isSlackConnected = chameleonCache.value.isSlackConnected;
      isHubspotConnected = chameleonCache.value.isHubspotConnected;
      isSalesforceConnected = chameleonCache.value.isSalesforceConnected;
      isLinkedinConnected = chameleonCache.value.isLinkedinConnected;
      icpDefinition = chameleonCache.value.icpDefinition;
    } else {
      await ensureConnectionStatuses(['slack', 'hubspot', 'salesforce', 'linkedin']);

      // Fetch fresh non-connection data using allSettled for robustness
      const results = await Promise.allSettled([
        apiFetch('/segments/all-table', {query: {limit: 1000, offset: 0}}),
        apiFetch('/icp/definition'),
      ]);

      // Extract values with fallbacks — API returns {data: Segment[]} envelope
      const rawSegments = results[0].status === 'fulfilled' ? results[0].value : null;
      segments = rawSegments
        ? Array.isArray(rawSegments)
          ? (rawSegments as Segment[])
          : ((rawSegments as {data?: Segment[]}).data ?? [])
        : [];
      isSlackConnected = isIntegrationConnected('slack');
      isHubspotConnected = isIntegrationConnected('hubspot');
      isSalesforceConnected = isIntegrationConnected('salesforce');
      isLinkedinConnected = isIntegrationConnected('linkedin');
      icpDefinition =
        results[1].status === 'fulfilled'
          ? (results[1].value as IcpDefinitionResponse | null)
          : null;

      // Update cache
      chameleonCache.value = {
        segments,
        isSlackConnected,
        isHubspotConnected,
        isSalesforceConnected,
        isLinkedinConnected,
        icpDefinition,
        timestamp: Date.now(),
      };
    }

    const segmentsState = useState<{data: Segment[]; refresh: () => Promise<void>}>(
      'segments',
      () => ({
        data: segments,
        refresh: async () => {
          const fresh = (await apiFetch('/segments/all-table', {
            query: {limit: 1000, offset: 0},
          })) as {data?: Segment[]} | Segment[];
          segmentsState.value.data = Array.isArray(fresh) ? fresh : (fresh?.data ?? []);
        },
      })
    );

    useIcpDefinitionState(icpDefinition);

    linkedinIntegrationConnected.value = isLinkedinConnected;

    // Determine ICP configuration status
    const isIcpConfigured =
      (icpDefinition?.buyer_persona?.length ?? 0) > 0 ||
      (icpDefinition?.target_account_lists?.length ?? 0) > 0;

    // Calculate renewal date (Stripe subscription current_period_end is in seconds)
    const renewalDate = org.value?.subscription?.current_period_end
      ? new Date(org.value.subscription.current_period_end * 1000).toISOString()
      : null;

    const chameleonPayload = {
      uid_hash: userHash, // sha256 hash of user ID for security
      email: auth.user.email,
      name: `${auth.user.firstName} ${auth.user.lastName}`,
      created: auth.user.createdAt, // ISO8601 format
      role: plan.planName, // Using plan as role
      logins: auth.user.loginCount || 0, // Login count if available
      project: activeOrgId, // Current organization ID
      // Additional Vector-specific properties
      login_method: auth.user.loginMethod,
      has_paid_plan: plan.hasPaidPlan,
      is_annual_plan: plan.isAnnualPlan,
      // New properties requested
      renewal_date: renewalDate,
      segment_count: Array.isArray(segments) ? segments.length : 0,
      last_login: auth.user.lastLoginAt || null,
      joined_at: auth.user.createdAt,
      slack_connected: isSlackConnected,
      hubspot_connected: isHubspotConnected,
      salesforce_connected: isSalesforceConnected,
      linkedin_connected: isLinkedinConnected,
      icp_configured: isIcpConfigured,
      pixel_installed: org.value?.is_pixel_installed || false,
    };

    const chmln = (window as WindowWithIntegrations).chmln;
    if (process.client) {
      console.log('[Chameleon] Full payload for identify:', {
        userId: auth.user.userId,
        payload: chameleonPayload,
        chmlnAvailable: !!chmln,
      });
    }

    chmln?.identify(auth.user.userId, chameleonPayload);
  } catch (error) {
    console.warn('[Chameleon] Failed to identify user:', error);
  }

  // Only set Pylon chat settings in production
  if (!isDev) {
    const hashResult = (await apiFetch(`/pylon/email-hash`, {
      method: 'POST',
      body: {email: auth.user.email},
    }).catch((err: unknown) => {
      if (process.client) {
        console.warn('[Pylon] email-hash request failed:', err);
      }
      return null;
    })) as PylonEmailHashResponse | null;

    const emailHash = hashResult?.emailHash ?? '';

    (window as WindowWithIntegrations).pylon = {
      chat_settings: {
        app_id: '2e6349d2-09e8-49ee-8756-656cf20a7c8c',
        email: auth.user.email,
        name: `${auth.user.firstName} ${auth.user.lastName}`,
        email_hash: emailHash,
      },
    };
  }
} else if (process.client) {
  console.log('[Chameleon] Skipped - no authenticated user (auth?.user is falsy)');
}

// Only mount ConfirmationModal when it should be shown (avoids segment delete modal on page load)
const isConfirmationModalOpen = useState('isConfirmationModalOpen', () => false);
const confirmationModalProps = useState<{title?: string}>('confirmationModalProps', () => ({}));
const showConfirmationModal = computed(
  () => !!isConfirmationModalOpen.value && !!confirmationModalProps.value?.title
);
</script>

<template>
  <NuxtLoadingIndicator />
  <ClientOnly>
    <UApp>
      <UMain>
        <NuxtLayout>
          <NuxtPage />
        </NuxtLayout>
      </UMain>
      <ConfirmationModal v-if="showConfirmationModal" />
    </UApp>
  </ClientOnly>
</template>

<style>
#vbackground {
  background: radial-gradient(circle at top, #3e6aee, #ffffff, #ffffff);
  background-size: 200% 100%;
  background-position: center -300px;
  background-repeat: no-repeat;
}

.PylonChat-bubbleFrameContainer {
  bottom: 32px !important;
}
.PylonChat-chatWindowFrameContainer {
  bottom: 32px !important;
}

/* UTable loading */
tr td.absolute {
  bottom: 0 !important;
  z-index: 1 !important;
}

progress {
  height: 3px !important;
  line-height: 3px !important;
}

progress.bar-animation-carousel::-webkit-progress-bar {
  background-color: transparent !important;
}

progress.bar-animation-carousel::-webkit-progress-bar.dark,
.dark progress.bar-animation-carousel::-webkit-progress-bar {
  background-color: transparent !important; /* or a lighter red if needed */
}

/* Firefox support */
progress.bar-animation-carousel {
  background-color: transparent !important;
}

.dark progress.bar-animation-carousel {
  background-color: transparent !important;
}
</style>
